import IntersectionObsManager from "../managers/IntersectionObserver.js";
import { onReducedMotionChange, prefersReducedMotion } from "../helpers/reducedMotion.js";

// The "What you can build" panel on /developer-program/ — three example builds behind a
// tab strip, with arrows and an autoplay.
//
// Enhancement, not construction. The markup ships every panel visible and stacked, with
// the tab strip, the arrows and the progress rail all `hidden`; this module reveals those
// three and only then starts hiding panels. So the no-JS state is a complete section
// rather than a broken carousel, and nothing on the page is ever a control that does
// nothing. Undoing that ordering — hiding panels first, or shipping the tabs visible —
// is what turns a bundle failure into an unusable section.
//
// Not built on Flickity like modules/slider.js. Flickity owns its own DOM and draws its
// own round dots; this wants labelled chips, a segmented rail, and real tab semantics
// (tablist/tab/tabpanel with a roving tabindex), which is markup Flickity would fight.
// What is borrowed from slider.js is the autoplay discipline, and for the same reasons —
// see the notes on the handlers below.

const AUTOPLAY_MS = 8000;

// Has to *clear* the .use-cases__panel opacity transition in _use-cases.scss (260ms), not
// merely approximate it — swapping early cuts the outgoing panel off mid-fade, which is
// the glitch this pair exists to prevent. The 20ms margin is the same one word-cycle.js
// uses for its 320ms transition. This read 240 against a 260ms transition until #188
// review; the comment claimed they matched and they did not.
const FADE_MS = 280;

class UseCases {
    constructor(elem) {
        this.elem = elem;
        this.tabs = [...elem.querySelectorAll('[role="tab"]')];
        this.panels = [...elem.querySelectorAll("[data-use-cases-panel]")];
        this.rail = elem.querySelector("[data-use-cases-rail]");
        this.segments = this.rail ? [...this.rail.children] : [];
        this.tabStrip = elem.querySelector("[data-use-cases-tabs]");
        this.index = 0;
        this.autoplay = null;
        this.fadeTimer = null;
        this.autoplayEnded = false;

        // Every tab needs its panel and vice versa; a mismatch means the template changed
        // under the module, and switching would then hide a panel it cannot bring back.
        // Leaving the stacked state alone is the safe failure.
        if (!this.tabs.length || this.tabs.length !== this.panels.length) return;

        this.reveal();
        this.bind();
        this.show(0, { animate: false });

        if (!prefersReducedMotion()) this.startAutoplay();
    }

    // Order matters: reveal the controls before hiding any panel, so there is no frame in
    // which the section has one visible panel and no way to reach the other two.
    reveal() {
        if (this.tabStrip) this.tabStrip.hidden = false;
        if (this.rail) this.rail.hidden = false;
        this.elem.querySelectorAll("[data-use-cases-arrows]").forEach((group) => {
            group.hidden = false;
        });

        // Last, and only after the controls exist. This is the flag _use-cases.scss reads
        // to switch from the plain stack to the one-panel-at-a-time presentation — the
        // grid that keeps the frame a constant height, and the suppression of the
        // between-panel dividers that only make sense while every panel is on the page.
        this.elem.classList.add("is-enhanced");
    }

    bind() {
        this.tabs.forEach((tab, i) => {
            tab.addEventListener("click", () => {
                this.endAutoplay();
                this.show(i);
                // Click already moves focus to the tab; this keeps keyboard activation
                // (Enter/Space, which also arrive as click) landing in the same place.
                tab.focus();
            });
        });

        // Roving tabindex: one tab in the page tab order at a time, arrows move between
        // them. Home/End included because a three-tab strip is small enough that jumping
        // to either end is a reasonable thing to want and costs two lines.
        this.tabs.forEach((tab) => tab.addEventListener("keydown", this.handleKeydown));

        this.elem.querySelectorAll("[data-use-cases-prev]").forEach((button) => {
            button.addEventListener("click", () => {
                this.endAutoplay();
                this.step(-1);
            });
        });

        this.elem.querySelectorAll("[data-use-cases-next]").forEach((button) => {
            button.addEventListener("click", () => {
                this.endAutoplay();
                this.step(1);
            });
        });

        onReducedMotionChange(this.handleMotionPreference);
    }

    handleKeydown = (event) => {
        const moves = {
            ArrowRight: 1,
            ArrowDown: 1,
            ArrowLeft: -1,
            ArrowUp: -1,
        };

        let next = null;

        if (event.key in moves) {
            next = (this.index + moves[event.key] + this.tabs.length) % this.tabs.length;
        } else if (event.key === "Home") {
            next = 0;
        } else if (event.key === "End") {
            next = this.tabs.length - 1;
        }

        if (next === null) return;

        // Only now, so an unhandled key — Tab most of all — keeps its default behaviour.
        event.preventDefault();
        this.endAutoplay();
        this.show(next);
        this.tabs[next].focus();
    };

    // Narrower than "any interaction", the same call slider.js makes: the visitor taking
    // the wheel retires the timer for the session, but merely scrolling past does not.
    endAutoplay() {
        if (this.autoplayEnded) return;

        this.autoplayEnded = true;
        this.stopAutoplay();
        if (this.observer) this.observer.unobserve(this.elem);
    }

    startAutoplay() {
        this.observer = new IntersectionObsManager({ rootMargin: "0% 0% 0% 0%", threshold: 0 });
        this.observer.observe(this.elem, this.handleIntersection);
    }

    stopAutoplay() {
        clearInterval(this.autoplay);
        this.autoplay = null;
    }

    // Offscreen, the timer stops rather than pauses. A panel rotating where nobody can see
    // it is wasted, and worse, it means a visitor scrolling back finds a different panel
    // than the one they left.
    handleIntersection = (entry) => {
        if (this.autoplayEnded) return;

        if (entry.isIntersecting) {
            if (!this.autoplay) this.autoplay = setInterval(() => this.step(1), AUTOPLAY_MS);
        } else {
            this.stopAutoplay();
        }
    };

    // One-way, as in slider.js. The preference turning on has to stop a rotation already
    // running; turning it back off deliberately does not restart one, because a visitor
    // who has been reading a static panel should not have it start moving under them.
    handleMotionPreference = () => {
        if (prefersReducedMotion()) this.endAutoplay();
    };

    step(delta) {
        this.show((this.index + delta + this.panels.length) % this.panels.length);
    }

    show(next, { animate = true } = {}) {
        if (next === this.index && animate) return;

        clearTimeout(this.fadeTimer);

        const swap = () => {
            this.panels.forEach((panel, i) => {
                // A class, not `panel.hidden`. The inactive panels have to keep their
                // layout box so the grid stack in _use-cases.scss can size the frame to
                // the tallest of them; `display: none` would collapse it and put the
                // page-shifting jump back. `visibility` still takes them out of the
                // accessibility tree and the tab order.
                panel.classList.toggle("is-inactive", i !== next);
                panel.classList.remove("is-fading");
            });

            this.tabs.forEach((tab, i) => {
                tab.setAttribute("aria-selected", String(i === next));
                // -1 rather than removing the attribute: a tab that is not current must
                // stay reachable by the arrow keys but out of the page's Tab order.
                tab.tabIndex = i === next ? 0 : -1;
            });

            this.segments.forEach((segment, i) => segment.classList.toggle("is-active", i === next));

            this.index = next;
        };

        if (!animate || prefersReducedMotion()) {
            swap();
            return;
        }

        this.panels[this.index].classList.add("is-fading");
        this.fadeTimer = setTimeout(swap, FADE_MS);
    }
}

document.querySelectorAll("[data-use-cases]").forEach((elem) => new UseCases(elem));
